import React, { useState, useEffect, useRef } from 'react';
import {
  BellIcon,
  XMarkIcon,
  ChevronDownIcon,
  UserCircleIcon,
  Cog6ToothIcon,
  ArrowLeftOnRectangleIcon,
  SunIcon,
  MoonIcon,
  Bars3Icon,
  HomeIcon,
  ChartBarIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  CalendarIcon,
  FolderIcon,
  UsersIcon,
  ShoppingCartIcon,
  AdjustmentsHorizontalIcon,
  UserIcon,
  ArchiveBoxIcon,
  BanknotesIcon,
  BuildingLibraryIcon,
  ClipboardDocumentCheckIcon,
  CurrencyDollarIcon,
  DocumentChartBarIcon,
  IdentificationIcon,
  ScaleIcon,
  ShieldCheckIcon,
  TableCellsIcon,
  TicketIcon,
  UserGroupIcon,
  WrenchScrewdriverIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import sessionCheck from "../session/userData";

const Header = () => {
  const [darkMode, setDarkMode] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const notificationsRef = useRef(null);
  const profileRef = useRef(null);
  const sidebarRef = useRef(null);

  const storedUserData = localStorage.getItem("user_data");
  const parsedData = JSON.parse(storedUserData);
  const [access_data, setAccess_data] = useState([]);
  const [userProfile, setUserProfile] = useState([]);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [modalAnimation, setModalAnimation] = useState(false);

  // Function to get appropriate icon based on menu title
  const getIconForMenu = (title) => {
    switch (title.toLowerCase()) {
      case 'dashboard':
        return HomeIcon;
      case 'user':
        return UserGroupIcon;
      case 'orders':
        return ShoppingCartIcon;
      case 'disputes':
        return ScaleIcon;
      case 'operators':
        return IdentificationIcon;
      case 'commission plan':
        return CurrencyDollarIcon;
      case 'provider':
        return BuildingLibraryIcon;
      case 'reports':
        return DocumentChartBarIcon;
      case 'regular user':
        return UserIcon;
      case 'dormat user':
        return ArchiveBoxIcon;
      case 'active user':
        return ShieldCheckIcon;
      case 'plans':
        return ClipboardDocumentCheckIcon;
      case 'margin slap':
        return AdjustmentsHorizontalIcon;
      case 'api balance':
        return BanknotesIcon;
      case 'sales report':
        return ChartBarIcon;
      case 'user interaction':
        return UsersIcon;
      case 'operator review':
        return TicketIcon;
      case 'provider review':
        return TableCellsIcon;
      case 'profit & loss':
        return DocumentTextIcon;
      default:
        return DocumentTextIcon;
    }
  };

  const menuItems = [
    { name: 'Dashboard', link: '/dashboard' },
    {
      name: 'User',
      submenu: [
        { name: 'Regular User', link: '/users' },
        { name: 'Top Topup', link: '/user-top-topup' },
        { name: 'Top Usage', link: '/user-top-usage' },
      ]
    },
    {
      name: 'Orders',
      submenu: [
        { name: 'All Orders', link: '/order-info' },
        { name: 'Live', link: '/order-info-live' },
        { name: 'Disputes', link: '/dispute-transaction' }
      ]
    },
    { name: 'Operators', link: '/operators' },
    {
      name: 'Commission',
      submenu: [
        { name: 'Plans', link: '/plans' },
        { name: 'Margin Slap', link: '/plan-slap' },
      ]
    },
    {
      name: 'Provider',
      submenu: [
        { name: 'Provider', link: '/providers' },
        { name: 'API Balance', link: '/provider-balance' },
      ]
    },
    {
      name: 'Utility',
      submenu: [
        { name: 'Banner', link: '/banner' },
        { name: 'Subscription', link: '/subscription' },
      ]
    },
    {
      name: 'Reports',
      submenu: [
        { name: 'Sales Report', link: '/report-sales' },
        { name: 'Provider Report', link: '/report-providers' },
        { name: 'Operator Report', link: '/report-operators' },
      ]
    }
  ].map(item => ({
    ...item,
    icon: getIconForMenu(item.name),
    submenu: item.submenu ? item.submenu.map(subItem => ({
      ...subItem,
      icon: getIconForMenu(subItem.name)
    })) : null
  }));

  useEffect(() => {
    if (sessionCheck() == null) {
      navigate("../login", { replace: true });
    } else {
      setAccess_data(JSON.parse(sessionCheck()));
    }
  }, []);

  useEffect(() => {
    if (access_data?.username) {
      setUserProfile(
        {
          name: parsedData.name || 'No name',
          email: parsedData.email || '',
          role: parsedData.role || '',
          avatar: parsedData.avatar || '',
        }
      )
    }
  }, [access_data?.username]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setMobileSidebarOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    document.documentElement.classList.toggle('dark', newDarkMode);
    localStorage.setItem('darkMode', String(newDarkMode));
  };

  const handleProfileToggle = () => {
    setProfileOpen(!profileOpen);
    setOpenSubmenu(null);
  };

  const toggleSubmenu = (menuName) => {
    setOpenSubmenu(openSubmenu === menuName ? null : menuName);
    setProfileOpen(false);
  };

  const toggleMobileSidebar = () => {
    setMobileSidebarOpen(!mobileSidebarOpen);
  };

  // Refresh function
  const handleRefresh = () => {
    // Force a hard refresh of the current page
    window.location.reload();
  };

  // Enhanced navigation function that handles same-page navigation
  const handleNavigation = (to) => {
    if (location.pathname === to) {
      // If same page, use the refresh function
      handleRefresh();
    } else {
      navigate(to);
    }
  };

  return (
    <>
      <header className="sticky top-0 z-40 h-16 bg-white dark:bg-gray-800 shadow-md">
        <div className="mx-auto max-w-7xl w-full flex items-center justify-between px-4 h-full">
          {/* Left Side - Logo and Menu Items */}
          <div className="flex items-center space-x-4">
            {/* Mobile Sidebar Toggle */}
            <div className="md:hidden">
              <button
                onClick={toggleMobileSidebar}
                className="p-1 text-gray-500 rounded-md dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
                aria-label="Open sidebar"
              >
                <Bars3Icon className="w-6 h-6" />
              </button>
            </div>

            {/* Logo/Brand - Hidden on mobile, shown on desktop */}
            <Link to="/" className="hidden md:block text-xl font-bold">
              <span className="text-indigo-600">C</span>
              <span className="text-pink-500">M</span>
              <span className="text-green-500">P</span>
            </Link>

            {/* Desktop Menu Items */}
            <div className="hidden md:flex items-center space-x-1">
              {menuItems.map((item) => (
                <div key={item.name} className="relative">
                  {item.submenu ? (
                    <>
                      <button
                        onClick={() => toggleSubmenu(item.name)}
                        className={`flex items-center px-3 py-2 text-sm font-medium rounded-md ${openSubmenu === item.name ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                      >
                        <item.icon className="w-4 h-4 mr-2" />
                        <span>{item.name}</span>
                        <ChevronDownIcon
                          className={`w-4 h-4 ml-1 transition-transform ${openSubmenu === item.name ? 'transform rotate-180' : ''}`}
                        />
                      </button>

                      {openSubmenu === item.name && (
                        <div className="absolute left-0 z-50 w-64 mt-2 origin-top-left bg-white rounded-md shadow-lg dark:bg-gray-700 ring-1 ring-black ring-opacity-5">
                          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-600">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {item.name}
                            </p>
                          </div>
                          <div className="grid grid-cols-3 gap-2 p-2">
                            {item.submenu.map((subItem) => (
                              <Link
                                key={subItem.name}
                                to={subItem.link}
                                className="flex flex-col items-center p-2 text-sm text-gray-700 rounded-md dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600"
                                onClick={() => setOpenSubmenu(null)}
                              >
                                {subItem.icon && <subItem.icon className="w-5 h-5 mb-1" />}
                                <span className="text-xs text-center">{subItem.name}</span>
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.link}
                      className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 rounded-md dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <item.icon className="w-4 h-4 mr-2" />
                      <span>{item.name}</span>
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Right-aligned controls */}
          <div className="flex items-center justify-end flex-1 gap-4">
            {/* Refresh Button */}
            <button
              onClick={handleRefresh}
              className="p-1 text-gray-500 rounded-md dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Refresh page"
              title="Refresh Page"
            >
              <ArrowPathIcon className="w-5 h-5" />
            </button>

            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className="p-1 text-gray-500 rounded-md dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {darkMode ? (
                <SunIcon className="w-5 h-5" />
              ) : (
                <MoonIcon className="w-5 h-5" />
              )}
            </button>

            {/* Profile Dropdown */}
            <div className="relative" ref={profileRef}>
              <button
                onClick={handleProfileToggle}
                className="flex items-center space-x-2 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-md"
                aria-label="User menu"
                aria-expanded={profileOpen}
              >
                {userProfile.avatar ? (
                  <img
                    className="w-6 h-6 rounded-full"
                    src={userProfile.avatar}
                    alt={`${userProfile.name}'s avatar`}
                  />
                ) : (
                  <UserCircleIcon className="w-8 h-8 text-gray-500 dark:text-gray-400" />
                )}
                <div className="hidden md:block">
                  <ChevronDownIcon
                    className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform ${profileOpen ? 'transform rotate-180' : ''}`}
                  />
                </div>
              </button>

              {profileOpen && (
                <div className="absolute right-0 z-50 w-48 mt-2 origin-top-right bg-white rounded-md shadow-lg dark:bg-gray-700 ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-600">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {userProfile.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {userProfile.email}
                    </p>
                  </div>
                  <ul className="py-1">
                    <li>
                      <Link
                        to='../my-profile'
                        className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600"
                      >
                        <UserCircleIcon className="w-4 h-4 mr-2" />
                        Your Profile
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/settings"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600"
                      >
                        <Cog6ToothIcon className="w-4 h-4 mr-2" />
                        Settings
                      </Link>
                    </li>
                  </ul>
                  <div className="py-1 border-t border-gray-200 dark:border-gray-600">
                    <button
                      onClick={() => {
                        setShowLogoutModal(true);
                        setTimeout(() => setModalAnimation(true), 10);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600"
                    >
                      <ArrowLeftOnRectangleIcon className="w-4 h-4 mr-2" />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar/Navigation Drawer */}
      <div
        className={`fixed inset-0 z-50 transform ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out md:hidden`}
      >
        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={toggleMobileSidebar}></div>
        <div
          ref={sidebarRef}
          className="relative w-4/5 max-w-xs h-full bg-white dark:bg-gray-800 shadow-xl"
        >
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <Link to="/" className="text-xl font-bold">
              <span className="text-indigo-600">C</span>
              <span className="text-pink-500">M</span>
              <span className="text-green-500">P</span>
            </Link>
            <button
              onClick={toggleMobileSidebar}
              className="p-1 text-gray-500 rounded-md dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Mobile Refresh Button */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => {
                handleRefresh();
                setMobileSidebarOpen(false);
              }}
              className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 rounded-md dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <ArrowPathIcon className="w-5 h-5 mr-3" />
              <span>Refresh Page</span>
            </button>
          </div>

          <div className="h-full overflow-y-auto">
            <nav className="p-4">
              <ul className="space-y-2">
                {menuItems.map((item) => (
                  <li key={item.name}>
                    {item.submenu ? (
                      <>
                        <button
                          onClick={() => toggleSubmenu(item.name)}
                          className={`flex items-center w-full px-3 py-2 text-sm font-medium rounded-md ${openSubmenu === item.name ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                        >
                          <item.icon className="w-5 h-5 mr-3" />
                          <span>{item.name}</span>
                          <ChevronDownIcon
                            className={`w-4 h-4 ml-auto transition-transform ${openSubmenu === item.name ? 'transform rotate-180' : ''}`}
                          />
                        </button>

                        {openSubmenu === item.name && (
                          <ul className="pl-8 mt-1 space-y-1">
                            {item.submenu.map((subItem) => (
                              <li key={subItem.name}>
                                <Link
                                  to={subItem.link}
                                  onClick={() => {
                                    setOpenSubmenu(null);
                                    setMobileSidebarOpen(false);
                                  }}
                                  className="flex items-center px-3 py-2 text-sm font-medium text-gray-600 rounded-md dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                >
                                  {subItem.icon && <subItem.icon className="w-4 h-4 mr-3" />}
                                  <span>{subItem.name}</span>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        )}
                      </>
                    ) : (
                      <Link
                        to={item.link}
                        onClick={() => setMobileSidebarOpen(false)}
                        className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 rounded-md dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <item.icon className="w-5 h-5 mr-3" />
                        <span>{item.name}</span>
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </div>
      </div>

      {/* Logout Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 transition-opacity duration-300">
          <div
            className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl transform transition-all duration-300 ${modalAnimation ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}
            style={{ width: '400px', maxWidth: '90%' }}
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Confirm Logout
                </h3>
                <button
                  onClick={() => {
                    setModalAnimation(false);
                    setTimeout(() => setShowLogoutModal(false), 300);
                  }}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Are you sure you want to log out? Any unsaved changes may be lost.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setModalAnimation(false);
                    setTimeout(() => setShowLogoutModal(false), 300);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    localStorage.clear();
                    navigate("../login", { replace: true });
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;